import React, { useEffect, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import classNames from 'classnames';

import { globalBridgeRef } from '@/arise/bridge/useAriseBridge';
import { sendEventV2 } from '@/utils/analytics';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { setCurrentlyViewingMedia } from '@/state/features/app';
import { LinkButton } from '@/components/Forms/Button';
import IconButton from '@/components/IconButton';
import { setMissionComplete } from '@/state/features/profile';
import { useTheme } from '@/theme';
import { setQuestCompleted, setQuestProgress } from '@/components/DiscordAuthFlow/useDiscord';
import { setQuestStatus } from '@/state/features/discord';
import { IS_DISCORD_ACTIVITY } from '@/globals';

import { VideoPlayer } from './VideoPlayer';
import { VideoPlayerType } from './VideoPlayer/videoUtils';
import { extractYouTubeVideoId } from './VideoPlayer/YoutubePlayer/youtubeUtils';
import { extractCloudflareVideoId } from './VideoPlayer/CloudflarePlayer/cloudflareUtils';
import { getABTestVideoUrl } from './VideoPlayer/abTesting';

import * as baseStyles from './styles.module.scss';

// Conditionally import theme-specific styles (which include base styles via @import)
const styles = (() => {
    switch (process.env.PUBLIC_INSTANCE) {
        case 'dakota':
            return require('./themeStyles/media-viewer-dakota.module.scss');
        // Add more theme-specific styles here as needed
        default:
            return baseStyles;
    }
})();

export default function MediaViewer() {
    const dispatch = useAppDispatch();
    const currentlyViewingMedia = useAppSelector((state) => state.app.currentlyViewingMedia);
    const modalRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const videoBorderImageUrl = theme.customJSON?.mediaViewer?.videoBorderImageUrl;

    useEffect(() => {
        if (currentlyViewingMedia?.resourceUrl) {
            sendEventV2('media_viewed', [currentlyViewingMedia.resourceType, currentlyViewingMedia.resourceUrl]);

            setTimeout(() => {
                modalRef.current?.focus();
            }, 200);
        }
    }, [currentlyViewingMedia?.resourceUrl]);

    if (!currentlyViewingMedia?.resourceUrl || !currentlyViewingMedia?.resourceType) return null;

    const ImageContainer = () => (
        <div className={styles.imageContainer}>
            <TransformWrapper
                limitToBounds={true}
                centerOnInit={true}
                doubleClick={{ mode: 'toggle' }}
                centerZoomedOut={true}
                pinch={{ step: 10 }}
                panning={{ velocityDisabled: true }}
            >
                <TransformComponent wrapperClass={styles.transformWrapper} contentClass={styles.transformContent}>
                    <img
                        className={styles.imageViewerImage}
                        src={currentlyViewingMedia?.resourceUrl || ''}
                        alt={currentlyViewingMedia?.description ?? ' '}
                    />
                </TransformComponent>
            </TransformWrapper>
            {currentlyViewingMedia?.description && (
                <p className={styles.imageDescription}>{currentlyViewingMedia.description}</p>
            )}
        </div>
    );

    const VideoContainer = () => {
        if (!currentlyViewingMedia?.resourceUrl) {
            console.warn('Cannot display content - no resource URL');
            return 'Cannot display content';
        }

        // ---- AB TEST CODE -----
        const originalUrl = currentlyViewingMedia.resourceUrl;
        const videoStorageKeyOverride = extractYouTubeVideoId(originalUrl);
        const cloudflareVideoPercent = theme.customJSON?.cloudflareVideoPercent;
        const url = getABTestVideoUrl(currentlyViewingMedia.resourceUrl, cloudflareVideoPercent);
        // ---- END OF AB TEST CODE -----

        let playerType: VideoPlayerType | null = null;
        let videoId: string | null = null;

        // Determine player type and extract video ID
        if (url.includes('youtube.com/watch') || url.includes('youtube.com/shorts/') || url.includes('youtu.be/')) {
            playerType = 'youtube';
            videoId = extractYouTubeVideoId(url);
        } else if (url.includes('cloudflarestream.com')) {
            playerType = 'cloudflare';
            videoId = extractCloudflareVideoId(url);
        }

        if (!playerType || !videoId) {
            console.warn('Unrecognised video type or invalid video URL');
            return 'Cannot display content';
        }

        return (
            <div className={styles.videoContainer}>
                <div className={styles.videoWrapper}>
                    <VideoPlayer
                        videoUrl={url}
                        videoId={videoId}
                        videoStorageKeyOverride={videoStorageKeyOverride}
                        playerType={playerType}
                        className={styles.videoIframe}
                        roundProgressBarClassName={styles.roundVideoProgressBar}
                        completionTextClassName={styles.completionText}
                        hasCompletionText={currentlyViewingMedia.requiredForMissionComplete}
                        preCompletionText="Complete the video to unlock reward"
                        postCompletionText="Video completed"
                        enforcedViewing={currentlyViewingMedia.enforcedViewing}
                        onMilestone={(val, isFirstTimeMilestoneReached) => {
                            if (currentlyViewingMedia.requiredForMissionComplete) {
                                // After 90% we will wait for 100 to complete quest
                                if (val <= 90 && isFirstTimeMilestoneReached) {
                                    if (IS_DISCORD_ACTIVITY) {
                                        // Only send each quest progress once ever as not critical and to avoid progress api spam
                                        setQuestProgress(val, dispatch);
                                    }
                                }
                            }
                        }}
                        onComplete={() => {
                            globalBridgeRef.current?.sendEvent('video-completed', currentlyViewingMedia);
                            if (currentlyViewingMedia.requiredForMissionComplete) {
                                dispatch(setMissionComplete());
                                if (IS_DISCORD_ACTIVITY) {
                                    // Send quest complete regardless of isFirstWatch in case previous completion failed
                                    setQuestCompleted(dispatch);
                                    dispatch(setQuestStatus('completed'));
                                }
                            }
                        }}
                    />
                </div>
                {currentlyViewingMedia?.description && (
                    <p className={styles.videoDescription}>{currentlyViewingMedia.description}</p>
                )}
            </div>
        );
    };

    const LinkContainer = () => {
        if (
            currentlyViewingMedia?.additionalImageUrl &&
            currentlyViewingMedia?.resourceUrl &&
            currentlyViewingMedia?.description
        ) {
            return (
                <>
                    <img
                        className={styles.externalLinkImage}
                        src={currentlyViewingMedia.additionalImageUrl}
                        alt=" "
                    ></img>
                    <LinkButton
                        className={styles.externalLinkButton}
                        href={currentlyViewingMedia.resourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {currentlyViewingMedia.description}
                    </LinkButton>
                </>
            );
        }
        console.warn('Cannot display externalLink');
        return 'Cannot display content';
    };

    const getMedia = () => {
        switch (currentlyViewingMedia?.resourceType) {
            case 'image':
                return ImageContainer();
            case 'video':
                return VideoContainer();
            case 'externalLink':
                return LinkContainer();
            default:
                console.warn('Cannot display content, unrecognised type: ' + currentlyViewingMedia?.resourceType);
                return 'Cannot display content';
        }
    };

    const onClose = () => {
        if (currentlyViewingMedia?.resourceType && currentlyViewingMedia?.resourceUrl) {
            sendEventV2('media_closed', [currentlyViewingMedia.resourceType, currentlyViewingMedia.resourceUrl]);
        }
        dispatch(setCurrentlyViewingMedia(null));
    };

    return (
        <div className={classNames(styles.MediaViewer, styles[currentlyViewingMedia?.resourceType])}>
            <div className={styles.overlay}></div>
            <div className={styles.modalContent} role="dialog" aria-label="Media viewer" ref={modalRef} tabIndex={-1}>
                <IconButton
                    iconType="close"
                    onClick={onClose}
                    className={styles.closeButton}
                    buttonStyle="round"
                    ariaLabel="Close Image"
                />
                <div
                    className={styles.mediaContainer}
                    style={videoBorderImageUrl ? { backgroundImage: `url('${videoBorderImageUrl}')` } : undefined}
                >
                    {getMedia()}
                </div>
            </div>
        </div>
    );
}

/* 
Can be activated using arise events such as:
arise.postMessage("view-media", {"resourceUrl": "http://www.link.com", "resourceType": "externalLink", "description": "Click me for stuff", "additionalImageUrl": "www.image.com/image.png" });
arise.postMessage("view-media", {"resourceUrl": "https://cdn.ariseworlds.com/uploads/97cab966-aa05-4e90-8b0b-f1448c318de5/16x9.webp", "resourceType": "image", "description": "Something"});
arise.postMessage("view-media", {"resourceUrl": "https://www.youtube.com/watch?v=vlxi3CwQd4Q", "resourceType": "video", "description": "Something"});
arise.postMessage("view-media", {"resourceUrl": "https://customer-c6kmyujkvesrdmhe.cloudflarestream.com/8518455b5c04c692392f39562eaf8fa2/iframe", "resourceType": "video", "enforcedViewing": true});
*/
